import { createRouter, createWebHistory } from 'vue-router'

import { useUserStore } from '@/stores/portal/user'
import { initAgentInfo } from '@/stores/portal/agentStore'
import { getAgentFromStorage } from '@/apps/portal/utils/agentUtils'
import { setAdminUnauthorizedHandler } from '@/api/clients/admin-client'
import { useAdminSessionStore } from '@/stores/admin/session'
import { useAdminApplicationStore } from '@/stores/admin/application'
import { useAdminNavigationStore } from '@/stores/admin/navigation'
import {
  ADMIN_BOOTSTRAP_DYNAMIC_ROUTES,
  registerAdminDynamicRoutes
} from './admin-dynamic-routes'

import { adminRoutes } from './modules/admin'
import { portalRoutes } from './modules/portal'

const base = import.meta.env.VITE_APP_BASE_URL || '/'

const router = createRouter({
  history: createWebHistory(base),
  routes: [...portalRoutes, ...adminRoutes]
})

registerAdminDynamicRoutes(router, ADMIN_BOOTSTRAP_DYNAMIC_ROUTES)

setAdminUnauthorizedHandler(() => {
  useAdminSessionStore().clearSession()
  useAdminApplicationStore().reset()
  useAdminNavigationStore().reset()

  if (
    router.currentRoute.value.path.startsWith('/admin') &&
    router.currentRoute.value.name !== 'admin-login'
  ) {
    void router.replace({
      name: 'admin-login',
      query: { redirect: router.currentRoute.value.fullPath }
    })
  }
})

router.beforeEach(async (to, _from, next) => {
  if (to.meta.appScope === 'admin') {
    const adminSession = useAdminSessionStore()

    if (to.name === 'admin-login') {
      if (adminSession.isAuthenticated) {
        const redirect = typeof to.query.redirect === 'string' &&
          to.query.redirect.startsWith('/admin') &&
          !to.query.redirect.startsWith('/admin/login')
          ? to.query.redirect
          : '/admin/dashboard'
        next(redirect)
      } else {
        next()
      }
      return
    }

    if (to.meta.requiresAdminAuth && !adminSession.isAuthenticated) {
      next({
        name: 'admin-login',
        query: { redirect: to.fullPath }
      })
      return
    }

    if (to.meta.skipAdminInitialization) {
      next()
      return
    }

    const application = useAdminApplicationStore()
    try {
      await application.initialize(router)
      const rematched = router.resolve(to.fullPath)
      if (to.name === 'admin-not-found' && rematched.name !== to.name) {
        next({ path: to.fullPath, replace: true })
      } else {
        next()
      }
    } catch {
      if (!adminSession.isAuthenticated) {
        next({ name: 'admin-login', query: { redirect: to.fullPath } })
      } else {
        next({
          name: 'admin-initialization-error',
          query: { redirect: to.fullPath }
        })
      }
    }

    return
  }

  const userStore = useUserStore()

  initAgentInfo().catch((error: unknown) => {
    console.error('初始化代理信息失败:', error)
  })

  if (to.meta.requiresAuth && !userStore.isLoggedIn) {
    const agentId = to.query.agent || getAgentFromStorage()

    if (agentId) {
      next({ path: '/login', query: { agent: agentId } })
    } else {
      next('/login')
    }
  } else {
    next()
  }
})

export default router
